import { AlertTriangle, Check, Clock3, MessageSquareText } from 'lucide-react'
import type {
  OperatorProjectionCommitmentDto,
  OperatorProjectionDependencyDto,
  OperatorProjectionIntervalDto,
  OperatorProjectionMilestoneDto,
  OperatorProjectionRiskDto,
  OperatorTimelineDto,
  OperatorZoom,
} from '../../services/operatorApi'
import type { OperatorFilters } from '../../stores/operatorStore'
import {
  GANTT_AXIS_HEIGHT,
  GANTT_ROW_HEIGHT,
  type GanttHierarchyRow,
} from './GanttHierarchy'

interface GanttTimelineProps {
  timeline: OperatorTimelineDto
  rows: GanttHierarchyRow[]
  filters: OperatorFilters
  zoom: OperatorZoom
  onEventSelect: (eventId: string) => void
}

const DAY_MS = 24 * 60 * 60 * 1000
const zoomWidth: Record<OperatorZoom, number> = {
  day: 58,
  week: 24,
  month: 11,
}

function ms(value: string): number {
  return new Date(value).getTime()
}

function formatAxisDate(value: number, zoom: OperatorZoom): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    month: zoom === 'month' ? 'short' : undefined,
    day: zoom === 'month' ? undefined : 'numeric',
    weekday: zoom === 'day' ? 'short' : undefined,
  }).format(new Date(value))
}

function statusMatches(status: string | null, filters: OperatorFilters): boolean {
  return filters.statuses.length === 0
    || (status !== null && filters.statuses.includes(status))
}

function horizonMatches(value: string, today: string, filters: OperatorFilters): boolean {
  if (filters.horizon === 'all') return true
  return filters.horizon === 'past'
    ? ms(value) < ms(today)
    : ms(value) >= ms(today)
}

function participantRowIndex(
  timeline: OperatorTimelineDto,
  rows: GanttHierarchyRow[],
  filters: OperatorFilters,
  participantId: string | null,
): number {
  if (!participantId) return 0
  if (
    filters.people.length > 0
    && !filters.people.includes(participantId)
  ) {
    return 0
  }
  const personIndex = rows.findIndex((row) =>
    row.kind === 'person' && row.personId === participantId)
  if (personIndex >= 0) return personIndex

  for (const party of timeline.hierarchy) {
    for (const team of party.teams) {
      if (!team.people.some(({ id }) => id === participantId)) continue
      const teamIndex = rows.findIndex((row) =>
        row.kind === 'team' && row.teamId === team.id)
      if (teamIndex >= 0) return teamIndex
    }
  }
  return 0
}

function endpointItem(
  records: {
    commitments: OperatorProjectionCommitmentDto[]
    milestones: OperatorProjectionMilestoneDto[]
  },
  endpoint: OperatorProjectionDependencyDto['source'],
): OperatorProjectionCommitmentDto | OperatorProjectionMilestoneDto | undefined {
  return endpoint.kind === 'commitment'
    ? records.commitments.find((item) => item.id === endpoint.id)
    : records.milestones.find((item) => item.id === endpoint.id)
}

function itemDate(
  item: OperatorProjectionCommitmentDto | OperatorProjectionMilestoneDto,
): string | null {
  return 'dueAt' in item ? item.dueAt : item.currentPlannedAt
}

function itemOwner(
  item: OperatorProjectionCommitmentDto | OperatorProjectionMilestoneDto,
): string | null {
  return 'accountableParticipantId' in item
    ? item.accountableParticipantId
    : item.ownerParticipantId
}

function waitingLabel(
  timeline: OperatorTimelineDto,
  dependency: OperatorProjectionDependencyDto,
): string | null {
  const source = endpointItem(timeline, dependency.source)
  if (!source || !('waitingPartyId' in source) || !source.waitingPartyId) return null
  const waiting = timeline.hierarchy.find((party) => party.id === source.waitingPartyId)
  const ownerId = itemOwner(source)
  const ownerParty = timeline.hierarchy.find((party) =>
    party.people.some((person) => person.id === ownerId)
    || party.teams.some((team) => team.people.some((person) => person.id === ownerId)))
  return waiting && ownerParty
    ? `${waiting.name} waiting on ${ownerParty.name}`
    : null
}

function partyParticipantIds(
  timeline: OperatorTimelineDto,
  partyId: string | null | undefined,
): string[] {
  if (!partyId) return []
  const party = timeline.hierarchy.find(({ id }) => id === partyId)
  if (!party) return []
  return [
    ...party.people.map(({ id }) => id),
    ...party.teams.flatMap((team) => team.people.map(({ id }) => id)),
  ]
}

function relevantParticipantIds(
  timeline: OperatorTimelineDto,
  item: OperatorProjectionIntervalDto | OperatorProjectionCommitmentDto,
): string[] {
  return [...new Set([
    item.accountableParticipantId,
    ...item.responsibleParticipantIds,
    ...('waitingPartyId' in item
      ? partyParticipantIds(timeline, item.waitingPartyId)
      : []),
  ].filter((id): id is string => id !== null))]
}

export interface VisibleTimelineRecords {
  events: OperatorTimelineDto['events']
  intervals: OperatorTimelineDto['intervals']
  commitments: OperatorTimelineDto['commitments']
  milestones: OperatorTimelineDto['milestones']
  dependencies: OperatorTimelineDto['dependencies']
  risks: OperatorProjectionRiskDto[]
}

export function visibleTimelineRecords(
  timeline: OperatorTimelineDto,
  rows: GanttHierarchyRow[],
  filters: OperatorFilters,
): VisibleTimelineRecords {
  const events = timeline.events.filter((event) => (
    (filters.eventTypes.length === 0 || filters.eventTypes.includes(event.eventType))
    && statusMatches(event.status, filters)
    && horizonMatches(event.startedAt, timeline.today, filters)
    && event.participantIds.some((participantId) =>
      participantRowIndex(timeline, rows, filters, participantId) > 0)
  ))
  const intervals = timeline.intervals.filter((interval) => (
    statusMatches(interval.status, filters)
    && horizonMatches(interval.startedAt, timeline.today, filters)
    && relevantParticipantIds(timeline, interval).some((participantId) =>
      participantRowIndex(timeline, rows, filters, participantId) > 0)
  ))
  const commitments = timeline.commitments.filter((commitment) => (
    Boolean(commitment.dueAt)
    && statusMatches(commitment.status, filters)
    && horizonMatches(commitment.dueAt ?? timeline.today, timeline.today, filters)
    && relevantParticipantIds(timeline, commitment).some((participantId) =>
      participantRowIndex(timeline, rows, filters, participantId) > 0)
  ))
  const milestones = timeline.milestones.filter((milestone) => (
    statusMatches(milestone.status, filters)
    && horizonMatches(milestone.currentPlannedAt, timeline.today, filters)
    && participantRowIndex(
      timeline,
      rows,
      filters,
      milestone.ownerParticipantId,
    ) > 0
  ))
  const dependencyRecords = { commitments, milestones }
  const dependencies = timeline.dependencies.filter((dependency) => {
    const source = endpointItem(dependencyRecords, dependency.source)
    const target = endpointItem(dependencyRecords, dependency.target)
    return Boolean(source && target && itemDate(source) && itemDate(target))
  })
  const commitmentIds = new Set(commitments.map(({ id }) => id))
  const dependencyIds = new Set(dependencies.map(({ id }) => id))
  const risks = timeline.risks.filter((risk) => (
    (risk.commitmentId === null || commitmentIds.has(risk.commitmentId))
    && (risk.dependencyId === null || dependencyIds.has(risk.dependencyId))
  ))
  return { events, intervals, commitments, milestones, dependencies, risks }
}

function participantName(
  timeline: OperatorTimelineDto,
  participantId: string,
): string {
  for (const party of timeline.hierarchy) {
    const person = [
      ...party.people,
      ...party.teams.flatMap((team) => team.people),
    ].find(({ id }) => id === participantId)
    if (person) return person.name
  }
  return participantId
}

interface LaneRole {
  participantId: string
  label: string
  accessibleLabel: string
}

function laneRoles(
  timeline: OperatorTimelineDto,
  item: OperatorProjectionIntervalDto | OperatorProjectionCommitmentDto,
): LaneRole[] {
  const roles = new Map<string, Set<'R' | 'A' | 'Waiting'>>()
  const add = (participantId: string | null, role: 'R' | 'A' | 'Waiting') => {
    if (!participantId) return
    roles.set(participantId, new Set([
      ...(roles.get(participantId) ?? []),
      role,
    ]))
  }
  for (const participantId of item.responsibleParticipantIds) {
    add(participantId, 'R')
  }
  add(item.accountableParticipantId, 'A')
  if ('waitingPartyId' in item) {
    for (const participantId of partyParticipantIds(
      timeline,
      item.waitingPartyId,
    )) {
      add(participantId, 'Waiting')
    }
  }
  return [...roles].flatMap(([participantId, value]) => {
    const ownership = value.has('R') && value.has('A')
      ? { label: 'R/A', accessibleLabel: 'R/A' }
      : value.has('A')
        ? { label: 'A', accessibleLabel: 'Accountable' }
        : value.has('R')
          ? { label: 'R', accessibleLabel: 'Responsible' }
          : null
    const waiting = value.has('Waiting')
    return [{
      participantId,
      label: ownership
        ? `${ownership.label}${waiting ? ' · Waiting' : ''}`
        : 'Waiting',
      accessibleLabel: ownership
        ? `${ownership.accessibleLabel}${waiting ? ' and Waiting' : ''}`
        : 'Waiting',
    }]
  })
}

function shortDate(value: string): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
  }).format(new Date(value))
}

function slippageLabel(days: number): string {
  if (days === 0) return 'On baseline'
  const unit = Math.abs(days) === 1 ? 'day' : 'days'
  return days > 0
    ? `${days} ${unit} late`
    : `${Math.abs(days)} ${unit} early`
}

export function GanttTimeline({
  timeline,
  rows,
  filters,
  zoom,
  onEventSelect,
}: GanttTimelineProps) {
  const rangeStart = ms(timeline.range.start)
  const rangeEnd = ms(timeline.range.end)
  const dayCount = Math.max(1, Math.ceil((rangeEnd - rangeStart) / DAY_MS))
  const width = Math.max(760, dayCount * zoomWidth[zoom])
  const height = GANTT_AXIS_HEIGHT + rows.length * GANTT_ROW_HEIGHT
  const x = (value: string) => {
    const ratio = (ms(value) - rangeStart) / Math.max(1, rangeEnd - rangeStart)
    return Math.max(0, Math.min(1, ratio)) * width
  }
  const y = (rowIndex: number) =>
    GANTT_AXIS_HEIGHT + rowIndex * GANTT_ROW_HEIGHT + GANTT_ROW_HEIGHT / 2

  const tickStep = zoom === 'day' ? 1 : zoom === 'week' ? 7 : 30
  const ticks = Array.from(
    { length: Math.ceil(dayCount / tickStep) + 1 },
    (_, index) => rangeStart + index * tickStep * DAY_MS,
  ).filter((value) => value <= rangeEnd)
  const {
    events,
    intervals,
    commitments,
    milestones,
    dependencies,
  } = visibleTimelineRecords(timeline, rows, filters)

  return (
    <div
      className="relative isolate overflow-hidden bg-background"
      style={{ width, height }}
      aria-label="Communication timeline"
    >
      <div
        className="absolute inset-x-0 top-0 border-b border-border bg-card"
        style={{ height: GANTT_AXIS_HEIGHT }}
      >
        {ticks.map((tick) => (
          <div
            key={tick}
            className="absolute inset-y-0 border-l border-border/70 px-1 pt-2 text-[9px] font-medium uppercase tracking-[0.08em] text-muted-foreground"
            style={{ left: x(new Date(tick).toISOString()) }}
          >
            {formatAxisDate(tick, zoom)}
          </div>
        ))}
      </div>

      {rows.map((row, index) => (
        <div
          key={row.id}
          className={`absolute inset-x-0 border-b border-border/60 ${
            row.kind === 'project'
              ? 'bg-muted/50'
              : row.kind === 'party'
                ? 'bg-muted/20'
                : ''
          }`}
          style={{
            top: GANTT_AXIS_HEIGHT + index * GANTT_ROW_HEIGHT,
            height: GANTT_ROW_HEIGHT,
          }}
        />
      ))}

      {ticks.map((tick) => (
        <div
          key={`grid-${tick}`}
          className="absolute bottom-0 border-l border-border/35"
          style={{
            left: x(new Date(tick).toISOString()),
            top: GANTT_AXIS_HEIGHT,
          }}
        />
      ))}

      {timeline.start && (
        <div
          data-testid="project-start"
          className="absolute bottom-0 z-20 border-l-2 border-emerald-500"
          style={{ left: x(timeline.start), top: GANTT_AXIS_HEIGHT }}
        >
          <span className="absolute left-1 top-1 whitespace-nowrap rounded-sm bg-emerald-600 px-2 py-1 text-[9px] font-bold uppercase tracking-[0.12em] text-white">
            Project start
          </span>
        </div>
      )}

      {timeline.today && (
        <div
          data-testid="today-marker"
          className="absolute bottom-0 z-20 w-px bg-primary"
          style={{ left: x(timeline.today), top: GANTT_AXIS_HEIGHT }}
        >
          <span className="absolute -left-5 top-1 rounded bg-primary px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-primary-foreground">
            Today
          </span>
        </div>
      )}

      {timeline.deadline && (
        <div
          data-testid="project-deadline"
          className="absolute bottom-0 z-20 border-l-2 border-dashed border-destructive"
          style={{ left: x(timeline.deadline), top: GANTT_AXIS_HEIGHT }}
        >
          <span className="absolute -left-20 top-1 w-40 rounded-sm bg-destructive px-2 py-1 text-center text-[9px] font-bold uppercase tracking-[0.12em] text-destructive-foreground">
            Final deadline
          </span>
        </div>
      )}

      {intervals.flatMap((interval) => laneRoles(timeline, interval)
        .filter(({ participantId }) =>
          participantRowIndex(timeline, rows, filters, participantId) > 0)
        .map((lane) => {
        const rowIndex = participantRowIndex(
          timeline,
          rows,
          filters,
          lane.participantId,
        )
        const left = x(interval.startedAt)
        return (
          <button
            key={`${interval.id}:${lane.participantId}`}
            type="button"
            tabIndex={0}
            className="absolute z-10 flex h-5 items-center gap-1 rounded-sm border border-primary/25 bg-primary/10 px-2 text-[9px] font-medium text-primary outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
            style={{
              left,
              top: y(rowIndex) - 10,
              width: Math.max(18, x(interval.endedAt) - left),
            }}
            title={`${interval.title}: ${interval.startedAt} – ${interval.endedAt}`}
            aria-label={`${interval.title}; ${lane.accessibleLabel}: ${participantName(timeline, lane.participantId)}`}
          >
            <span className="rounded-sm bg-primary px-1 text-[8px] font-bold text-primary-foreground">
              {lane.label}
            </span>
            <span className="truncate">{interval.title}</span>
          </button>
        )
      }))}

      {commitments.flatMap((commitment) => laneRoles(timeline, commitment)
        .filter(({ participantId }) =>
          participantRowIndex(timeline, rows, filters, participantId) > 0)
        .map((lane) => {
        const rowIndex = participantRowIndex(
          timeline,
          rows,
          filters,
          lane.participantId,
        )
        return (
          <button
            key={`${commitment.id}:${lane.participantId}:${lane.label}`}
            type="button"
            tabIndex={0}
            className="absolute z-20 -translate-x-1/2 -translate-y-1/2 outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
            style={{ left: x(commitment.dueAt ?? timeline.today), top: y(rowIndex) }}
            title={`${commitment.deliverable} · ${commitment.status}`}
            aria-label={`${commitment.deliverable}; ${lane.accessibleLabel}: ${participantName(timeline, lane.participantId)}; ${commitment.status}`}
          >
            <span className={`flex size-5 rotate-45 items-center justify-center rounded-[3px] border ${
              commitment.status === 'overdue' || commitment.status === 'blocked'
                ? 'border-destructive bg-destructive/15 text-destructive'
                : 'border-border bg-card text-foreground'
            }`}>
              <Clock3 size={10} className="-rotate-45" aria-hidden="true" />
            </span>
            <span className="absolute left-4 top-3 flex w-36 items-center gap-1 truncate text-[9px] font-medium text-foreground">
              <span className="rounded-sm bg-muted px-1 text-[8px] font-bold text-foreground">
                {lane.label}
              </span>
              {commitment.deliverable}
            </span>
          </button>
        )
      }))}

      {milestones.map((milestone) => {
        const rowIndex = participantRowIndex(
          timeline,
          rows,
          filters,
          milestone.ownerParticipantId,
        )
        const baseline = x(milestone.baselinePlannedAt)
        const current = x(milestone.currentPlannedAt)
        const completionFact = milestone.actualCompletedAt
          ? `; actual ${shortDate(milestone.actualCompletedAt)}; status ${milestone.status.replace(/_/g, ' ')}`
          : `; status ${milestone.status.replace(/_/g, ' ')}`
        return (
          <div key={milestone.id}>
            <span
              data-testid="milestone-baseline"
              className="absolute z-10 size-3 -translate-x-1/2 -translate-y-1/2 rotate-45 border border-muted-foreground/70 bg-background"
              style={{ left: baseline, top: y(rowIndex) }}
              title={`${milestone.title} baseline · ${milestone.baselinePlannedAt}`}
            />
            <span
              className="absolute z-0 h-px border-t border-dashed border-muted-foreground/60"
              style={{
                left: Math.min(baseline, current),
                top: y(rowIndex),
                width: Math.max(2, Math.abs(current - baseline)),
              }}
            />
            <button
              type="button"
              tabIndex={0}
              data-testid="milestone-current"
              className="absolute z-20 flex size-5 -translate-x-1/2 -translate-y-1/2 rotate-45 items-center justify-center rounded-[3px] border border-primary bg-primary text-primary-foreground"
              style={{ left: current, top: y(rowIndex) }}
              title={`${milestone.title} current plan · ${milestone.currentPlannedAt}`}
              aria-label={`${milestone.title}; baseline ${shortDate(milestone.baselinePlannedAt)}; current ${shortDate(milestone.currentPlannedAt)}; ${slippageLabel(milestone.slippageDays)}${completionFact}`}
            >
              <Check size={10} className="-rotate-45" aria-hidden="true" />
            </button>
            <span
              className="absolute z-20 w-48 text-[9px] font-semibold text-foreground"
              style={{ left: current + 9, top: y(rowIndex) - 19 }}
            >
              <span className="block truncate">{milestone.title}</span>
              <span className="block font-medium text-destructive">
                {slippageLabel(milestone.slippageDays)}
              </span>
              <span className="block font-normal text-muted-foreground">
                Baseline {shortDate(milestone.baselinePlannedAt)}
                {' → '}
                Current {shortDate(milestone.currentPlannedAt)}
              </span>
            </span>
            {milestone.actualCompletedAt && (
              <button
                type="button"
                tabIndex={0}
                data-testid="milestone-actual"
                aria-label={`${milestone.title}; actual completion ${shortDate(milestone.actualCompletedAt)}; status ${milestone.status.replace(/_/g, ' ')}`}
                className="absolute z-20 size-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-foreground outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
                style={{ left: x(milestone.actualCompletedAt), top: y(rowIndex) }}
                title={`${milestone.title} completed · ${milestone.actualCompletedAt}`}
              />
            )}
          </div>
        )
      })}

      {events.flatMap((event) => {
        const participantIds = event.participantIds.filter((participantId) =>
          participantRowIndex(timeline, rows, filters, participantId) > 0)
        return participantIds.map((participantId, eventIndex) => {
          const rowIndex = participantRowIndex(
            timeline,
            rows,
            filters,
            participantId,
          )
          return (
            <button
              key={`${event.id}:${participantId}`}
              type="button"
              tabIndex={0}
              onClick={() => onEventSelect(event.id)}
              className={`absolute z-30 flex -translate-x-1/2 -translate-y-1/2 items-center gap-1 rounded-full border px-1.5 py-1 text-[9px] font-semibold shadow-sm outline-none transition duration-150 hover:-translate-y-[55%] focus-visible:ring-2 focus-visible:ring-primary/60 motion-reduce:transition-none ${
                event.status === 'complete'
                  ? 'border-border bg-card text-foreground'
                  : 'border-primary/35 bg-primary/10 text-primary'
              }`}
              style={{ left: x(event.startedAt), top: y(rowIndex) }}
              aria-label={`${event.title ?? event.eventType}, open evidence`}
            >
              <MessageSquareText size={10} aria-hidden="true" />
              {eventIndex === 0 && (
                <span className="max-w-32 truncate">
                  {event.title ?? event.eventType}
                </span>
              )}
            </button>
          )
        })
      })}

      <svg
        className="pointer-events-none absolute inset-0 z-40 overflow-visible text-destructive"
        width={width}
        height={height}
        aria-label="Cross-lane dependencies"
      >
        <defs>
          <marker
            id="operator-dependency-arrow"
            markerWidth="7"
            markerHeight="7"
            refX="6"
            refY="3.5"
            orient="auto"
          >
            <path d="M0,0 L7,3.5 L0,7 Z" fill="currentColor" />
          </marker>
        </defs>
        {dependencies.map((dependency) => {
          const records = { commitments, milestones }
          const source = endpointItem(records, dependency.source)
          const target = endpointItem(records, dependency.target)
          const sourceDate = source ? itemDate(source) : null
          const targetDate = target ? itemDate(target) : null
          if (!source || !target || !sourceDate || !targetDate) return null
          const sourceRow = participantRowIndex(
            timeline,
            rows,
            filters,
            itemOwner(source),
          )
          const targetRow = participantRowIndex(
            timeline,
            rows,
            filters,
            itemOwner(target),
          )
          if (sourceRow <= 0 || targetRow <= 0) return null
          const x1 = x(sourceDate)
          const x2 = x(targetDate)
          const y1 = y(sourceRow)
          const y2 = y(targetRow)
          const controlX = (x1 + x2) / 2
          const label = waitingLabel(timeline, dependency)
          return (
            <g
              key={dependency.id}
              data-testid="dependency-connector"
              role="img"
              tabIndex={0}
              aria-label={label ?? `${dependency.type} dependency`}
            >
              <path
                d={`M ${x1} ${y1} C ${controlX} ${y1}, ${controlX} ${y2}, ${x2} ${y2}`}
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeDasharray={dependency.status === 'open' ? '4 3' : undefined}
                markerEnd="url(#operator-dependency-arrow)"
                opacity="0.82"
              />
              {label && (
                <g transform={`translate(${controlX - 90} ${(y1 + y2) / 2 - 10})`}>
                  <rect
                    width="180"
                    height="20"
                    rx="4"
                    className="fill-background stroke-destructive/40"
                  />
                  <text
                    x="90"
                    y="13"
                    textAnchor="middle"
                    className="fill-destructive text-[9px] font-semibold"
                  >
                    {label}
                  </text>
                </g>
              )}
            </g>
          )
        })}
      </svg>

      {events.length === 0 && intervals.length === 0
        && commitments.length === 0 && milestones.length === 0 && (
          <div className="absolute left-5 top-16 flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-xs text-muted-foreground">
            <AlertTriangle size={13} aria-hidden="true" />
            No timeline records match these filters.
          </div>
        )}
    </div>
  )
}
